// Schemas for routes-telnyx.ts (browser-safe zod). Telnyx connection + provider-neutral voice lines (plan B1).
//
// Honesty rules encoded here:
//  • every Telnyx capability carries a state; only the credential check and the numbers list come from the provider,
//    everything about calls (call control, inbound, media streaming, WebRTC, recordings) stays «sin verificar» until a
//    real account proves it;
//  • Telnyx webhooks are authenticated (Ed25519) and stored; since plan B2 call events drive AI calls on active lines
//    (dial/answer through LIA's Call Control), everything still «sin verificar» against a real account;
//  • the inbound policy of a line is `effective` only where it can be handled: «IA en horario» with the voice service,
//    «Asesores» with the softphone configured (plan B3: rings available advisors; nobody → rejected + callback task).
import { z } from 'zod';
import { CapabilityReportSchema } from './api.js';
import { ConnectionStatusSchema } from './enums.js';
import { ScheduleStatusSchema } from './schedule.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);

/** What LIA does with inbound calls on a line (ai_in_hours since B2; advisors rings the softphone since B3). */
export const TELNYX_INBOUND_POLICIES = ['off', 'ai_in_hours', 'advisors'] as const;
export const TelnyxInboundPolicySchema = z.enum(TELNYX_INBOUND_POLICIES);
export type TelnyxInboundPolicy = z.infer<typeof TelnyxInboundPolicySchema>;

export const TELNYX_INBOUND_POLICY_LABELS: Record<TelnyxInboundPolicy, string> = {
  off: 'No atender desde LIA',
  ai_in_hours: 'Agente de IA en horario (fuera de horario: tarea de devolución, nunca IA)',
  advisors: 'Asesores (softphone en LIA)',
};

/** Capability keys of a Telnyx connection (report stored on the connection like every provider). */
export const TELNYX_CAPABILITIES = ['account', 'lines', 'webhooks', 'call_control', 'inbound_calls', 'media_streaming', 'webrtc', 'recordings'] as const;
export type TelnyxCapability = (typeof TELNYX_CAPABILITIES)[number];

export const TELNYX_CAPABILITY_LABELS: Record<TelnyxCapability, string> = {
  account: 'Cuenta y clave de API',
  lines: 'Números visibles',
  webhooks: 'Eventos de Telnyx (webhooks firmados)',
  call_control: 'Control de llamadas (Call Control)',
  inbound_calls: 'Llamadas entrantes',
  media_streaming: 'Audio en tiempo real (media streaming)',
  webrtc: 'Softphone WebRTC para asesores',
  recordings: 'Grabaciones',
};

/**
 * Voicemail on «advisors» lines (plan B7, «sin verificar»): when nobody answers (or nobody is available / out of hours)
 * LIA answers, plays the greeting and records the message. OFF by default. The greeting itself is the consent notice:
 * it must say the message is recorded.
 */
export const VOICEMAIL_MAX_SECONDS = { min: 15, max: 300, default: 120 } as const;
export const VOICEMAIL_GREETING_MAX = 400;
export const DEFAULT_VOICEMAIL_GREETING =
  'Gracias por llamar. En este momento no podemos atenderte. Deja tu nombre y tu mensaje después del tono; tu mensaje será grabado para darte seguimiento.';
/** The greeting must announce the recording (grabado / grabará / grabación…). */
export const VOICEMAIL_GREETING_MENTIONS_RECORDING = /grab/i;

export const TelnyxVoicemailSchema = z.object({
  enabled: z.boolean(),
  maxSeconds: z.number().int(),
  /** Text played to the caller before the beep (custom or the default). */
  greeting: z.string(),
});
export type TelnyxVoicemail = z.infer<typeof TelnyxVoicemailSchema>;

export const UpdateTelnyxVoicemailSchema = z.object({
  enabled: z.boolean(),
  maxSeconds: z.number().int().min(VOICEMAIL_MAX_SECONDS.min).max(VOICEMAIL_MAX_SECONDS.max).optional(),
  /** null = back to the default greeting. */
  greeting: z
    .string()
    .trim()
    .min(20)
    .max(VOICEMAIL_GREETING_MAX)
    .refine((v) => VOICEMAIL_GREETING_MENTIONS_RECORDING.test(v), { message: 'El saludo debe avisar que el mensaje se graba' })
    .nullable()
    .optional(),
});
export type UpdateTelnyxVoicemail = z.infer<typeof UpdateTelnyxVoicemailSchema>;

export const TelnyxLineSchema = z.object({
  endpointId: Id,
  connectionId: Id,
  displayName: z.string(),
  phoneE164: z.string().nullable(),
  /** Telnyx phone number id (as returned by the numbers list). */
  providerNumberId: z.string(),
  /** Telnyx-side number status as read from the API (unverified field), e.g. "active". */
  providerStatus: z.string().nullable(),
  /** Telnyx connection/application the number is assigned to, as read from the API (unverified field). */
  providerConnectionName: z.string().nullable(),
  /** Telnyx Call Control application id of the number (unverified field): the `connection_id` LIA dials with. */
  providerConnectionId: z.string().nullable(),
  status: ConnectionStatusSchema,
  /** Active in LIA (status connected). A number can be active on one provider only. */
  active: z.boolean(),
  /** Why the line cannot be activated (same number active on Aircall / another Telnyx connection), null = none. */
  conflict: z.string().nullable(),
  healthMessage: z.string().nullable(),
  shared: z.boolean(),
  projectIds: z.array(Id),
  defaultProjectId: Id.nullable(),
  inboundPolicy: TelnyxInboundPolicySchema,
  /** Active line with a Call Control application and either «IA en horario» + voice service, or «Asesores» + softphone configured. */
  inboundPolicyEffective: z.boolean(),
  /** Plan B7: voicemail when an «advisors» line call is not answered (only used with that policy). */
  voicemail: TelnyxVoicemailSchema.default({ enabled: false, maxSeconds: VOICEMAIL_MAX_SECONDS.default, greeting: DEFAULT_VOICEMAIL_GREETING }),
  /** Voice operating schedule that applies to the line's default project (same resolution as Aircall lines). */
  schedule: ScheduleStatusSchema,
});
export type TelnyxLine = z.infer<typeof TelnyxLineSchema>;

export const TelnyxConnectionSetupSchema = z.object({
  connectionId: Id,
  label: z.string(),
  status: ConnectionStatusSchema,
  capabilities: CapabilityReportSchema,
  numbers: z.object({ syncedAt: Iso.nullable(), total: z.number().int().nullable() }),
  webhook: z.object({
    callbackUrl: z.string(),
    publicKeyConfigured: z.boolean(),
    /** First characters of the stored public key (public material; shown to recognise which key is saved). */
    publicKeyPreview: z.string().nullable(),
    publicKeySavedAt: Iso.nullable(),
    lastEventAt: Iso.nullable(),
    toleranceSeconds: z.number().int(),
  }),
  /** Fixed notice: what is done with received events (and that nothing is verified with a real account). */
  processingNotice: z.string(),
  lines: z.array(TelnyxLineSchema),
});
export type TelnyxConnectionSetup = z.infer<typeof TelnyxConnectionSetupSchema>;

export const TelnyxSetupSchema = z.object({ items: z.array(TelnyxConnectionSetupSchema) });
export type TelnyxSetup = z.infer<typeof TelnyxSetupSchema>;

/** Telnyx Mission Control › Keys & Credentials › Public Key (base64 Ed25519 public key; PEM also accepted). */
export const SetTelnyxWebhookKeySchema = z.object({ publicKey: z.string().trim().min(40).max(400) });

export const UpdateTelnyxLineSchema = z
  .object({ active: z.boolean(), inboundPolicy: TelnyxInboundPolicySchema, voicemail: UpdateTelnyxVoicemailSchema })
  .partial()
  .refine((v) => v.active !== undefined || v.inboundPolicy !== undefined || v.voicemail !== undefined, { message: 'Indica qué cambiar' });

export const TelnyxWebhookAckSchema = z.object({ received: z.boolean(), duplicate: z.boolean() });
